// @flow
import * as React from 'react';
import {
  useChangeMedigapEnrollmentStatusMutation,
  useDeleteMedigapEnrollmentMutation,
  useSaveMedigapEnrollmentMutation, ZipSelector
} from '../../../../../shared';
import { forwardRef, useEffect, useState } from 'react';
import {
  Box, Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemText,
  MenuItem,
  Stack, Typography
} from '@mui/material';
import { ModalCloseButton } from '@coverright/ui/shared';
import { Copy, Pen, X } from '@phosphor-icons/react';
import * as yup from "yup";
import { useFormik } from 'formik';
import {
  formatToPhoneNumber,
  FormikDateInput,
  FormikSelectInput,
  FormikTextInput,
  PhoneNumberInput
} from '@coverright/ui/inputs';
import moment from 'moment';
import { useConfirm } from '@coverright/features';
import { AgentSelector } from '../../../../../features';
import { MgCarrierSelector } from './carrier-selector';
import { LoadingButton } from '@mui/lab';
import { covertDateToSeverFormat, isEffectiveDateValid } from '@coverright/utils';
import _ from 'lodash';
import { useClient } from '../../../hooks/client-context';
import { theme } from '../../../../../app/theme';
import { MgPlanSelector } from './plan-selector/mg-plan-selector';
import { adjustAge, getAgeOnEffectiveDate, getMgEnrollmentTypeSubTitle, getMgEnrollmentTypeTitle } from '../lib';
import {
  EnrollmentListItemOutput, EnrollmentStatusDto,
  Gender,
  MedigapEnrollmentPlanName, MedigapEnrollmentStatus,
  MedigapEnrollmentType
} from '@coverright/data-access/types/enrollment';


type Props = {
  enrollment?: Partial<EnrollmentListItemOutput>;
  onSubmit: (refresh?: boolean) => void
};
const MgEnrollmentForm = ({enrollment = initial, onSubmit}: Props) => {
  const client = useClient();
  const [saveEnrollment, {loading: saveLoading}] = useSaveMedigapEnrollmentMutation();
  const [changeStatus, {loading: changeStatusLoading}] = useChangeMedigapEnrollmentStatusMutation();
  const formik = useFormik({
    initialValues: {
      ...enrollment,
      phoneNumber: formatToPhoneNumber(enrollment.phoneNumber),
      planKey: enrollment?.mgPlanKey,
      birthDate: enrollment.birthDate ? moment.utc(enrollment.birthDate).format('MM/DD/YYYY') : '',
      effectiveDate: enrollment.effectiveDate ? moment.utc(enrollment.effectiveDate).format('MM/DD/YYYY') : '',
      applicationDate: enrollment.applicationDate ? moment.utc(enrollment.applicationDate).format('MM/DD/YYYY') : '',
    },
    validationSchema,
    validateOnChange: true,
    validateOnMount: true,
    onSubmit: async values => {
      const fields = _.pick(values, ['zip', 'county', 'phoneNumber', 'firstName', 'lastName', 'email', 'agentId', 'carrierName', 'planName', 'naic', 'planKey']);

      if (!fields.planKey || !fields.naic) {
        throw new Error('Field is required');
        return
      }

      const result = await saveEnrollment({
        variables: {
          input: {
            ...fields,
            clientId: enrollment.id ? undefined : client.id,
            planName: values.planName as MedigapEnrollmentPlanName,
            gender: values.gender as Gender,
            age: parseFloat(String(values.age)),
            tobacco: String(values.tobacco) === 'true',
            id: enrollment.id,
            naic: fields.naic!,
            planKey: fields.planKey!,
            phoneNumber: fields.phoneNumber.replace(/\s|\(|\)|-/gm, ''),
            enrollmentType: values.enrollmentType as MedigapEnrollmentType,
            applicationDate: covertDateToSeverFormat(values.applicationDate),
            effectiveDate: covertDateToSeverFormat(values.effectiveDate),
            birthDate: covertDateToSeverFormat(values.birthDate),
          }
        }
      });
      await changeStatus({
        variables: {
          status: MedigapEnrollmentStatus.Draft,
          enrollmentId: result.data?.saveMedigapEnrollment
        }
      })

      onSubmit(true);
    },
  });

  useEffect(() => {
    if (formik.values.zip?.length && formik.values.zip.length < 5) {
      formik.setFieldValue('county', '');
      formik.setFieldValue("carrierName", '');
    }
  }, [formik.values.zip]);

  useEffect(() => {
    if (isEffectiveDateValid(formik.values.effectiveDate) && moment(formik.values.birthDate).isValid()) {
      formik.setFieldValue('age', getAgeOnEffectiveDate(formik.values.birthDate as string, formik.values.effectiveDate as string));
    }
  }, [formik.values.effectiveDate, formik.values.birthDate]);

  return (
    <Stack component={'form'} gap={2} onSubmit={formik.handleSubmit}>
      <Box sx={{display: 'flex', gap: 2}}>
        <FormikTextInput formik={formik}
                         fullWidth
                         name={'firstName'} />
        <FormikTextInput formik={formik}
                         fullWidth
                         name={'lastName'} />
        <FormikTextInput formik={formik}
                         name={'email'} />
      </Box>
      <Box sx={{display: 'flex', gap: 2}}>
        <FormikDateInput formik={formik}
                         name={'birthDate'} />
        <PhoneNumberInput value={formik.values.phoneNumber}
                          label={'Phone number'}
                          error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                          helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                          placeholder={'+1 (000) 000-0000'}
                          onChange={e => (
                            formik.setFieldValue('phoneNumber', e.target.value)
                          )} />
        <ZipSelector onChange={(data) => {
          formik.setFieldValue('zip', data.zip);
          formik.setFieldValue('county', data.county);
        }}
                            inputProps={{
                              label: 'Zip code',
                              error: formik.touched.zip && Boolean(formik.errors.zip) || formik.touched.county && Boolean(formik.errors.county),
                              helperText: formik.touched.zip && formik.errors.zip || formik.touched.county && formik.errors.county
                            }}
                            zip={formik.values.zip || ''}
                            county={formik.values.county || ''} />
      </Box>
      <Box sx={{display: 'flex', gap: 2}}>
        <FormikTextInput formik={formik}
                         fullWidth
                         name={'age'} />

        <FormikSelectInput formik={formik} name={'tobacco'}
                           value={formik.values.tobacco !== null ? String(formik.values.tobacco) : ''}
                           onChange={(event: any) => {
                             formik.setFieldValue('tobacco', event.target.value === 'true');
                           }}>
          <MenuItem disabled sx={{display: 'none'}} value={''}></MenuItem>
          <MenuItem value={'true'}>Yes</MenuItem>
          <MenuItem value={'false'}>No</MenuItem>
        </FormikSelectInput>

        <FormikSelectInput value={formik.values.gender ? formik.values.gender : ''} formik={formik} name={'gender'}>
          <MenuItem disabled sx={{display: 'none'}} value={''}></MenuItem>
          <MenuItem value={Gender.M}>Male</MenuItem>
          <MenuItem value={Gender.F}>Female</MenuItem>
        </FormikSelectInput>
      </Box>

      <Divider sx={{mt: 1, mx: -3}} >
        <Typography variant={'caption'} color={'text.secondary'}>Enrollment related info</Typography>
      </Divider>

      <Box sx={{display: 'flex', gap: 2}}>
        <FormikDateInput formik={formik}
                         helperText={(enrollment.effectiveDate || formik.touched['effectiveDate']) && formik.errors['effectiveDate']}
                         name={'effectiveDate'} />
        <FormikDateInput formik={formik}
                         fullWidth
                         name={'applicationDate'} />
        <AgentSelector hideTick={false}
                       value={formik.values.agentId}
                       onChange={(agentId) => {
                         formik.setFieldValue("agentId", agentId);
                       }}
                       error={formik.touched.agentId && Boolean(formik.errors.agentId) ? formik.errors.agentId as string : undefined}/>
        {/*<FormikSelectInput formik={formik} name={'status'}>
          <MenuItem value={EnrollmentStatusDto.Draft}>Draft</MenuItem>
          <MenuItem value={EnrollmentStatusDto.Enrolled}>Enrolled</MenuItem>
          <MenuItem value={EnrollmentStatusDto.New}>New</MenuItem>
          <MenuItem value={EnrollmentStatusDto.Rejected}>Rejected</MenuItem>
          <MenuItem value={EnrollmentStatusDto.Sent}>Sent</MenuItem>
        </FormikSelectInput>*/}

      </Box>


      <Box sx={{display: 'flex', gap: 2}}>

        <MgCarrierSelector zip={formik.values.zip || ''}
                           county={formik.values.county || ''}
                           gender={formik.values.gender as Gender}
                           tobacco={typeof formik.values.tobacco === 'boolean' ? formik.values.tobacco : undefined}
                           value={formik.values.carrierName || ''}
                           effectiveDate={formik.values.effectiveDate ? moment(formik.values.effectiveDate).format('YYYY-MM-DD') : undefined}
                           disabled={!(formik.values.zip && formik.values.county && formik.values.effectiveDate && formik.values.tobacco !== null && formik.values.gender)}
                           onChange={data => {
                             formik.setFieldValue("carrierName", data.name);
                             formik.setFieldValue("naic", data.naic);
                             formik.setFieldValue("plan", '');
                             formik.setFieldValue("planKey", '');
                             formik.setFieldValue("planName", '');
                           }}
                           age={adjustAge(String(formik.values.age) || '', formik.values.birthDate, formik.values.effectiveDate)}
        />
        <MgPlanSelector zip={formik.values.zip || ''}
                        county={formik.values.county || ''}
                        value={formik.values.planKey || ''}
                        planName={formik.values.planName}
                        onChange={plan => {
                          formik.setFieldValue("planKey", plan.key);
                          formik.setFieldValue("naic", plan.naic)
                          formik.setFieldValue("planName", plan.planName)
                        }}
                        gender={formik.values.gender as Gender}
                        tobacco={String(formik.values.tobacco) === 'true'}
                        effectiveDate={formik.values.effectiveDate ? moment.utc(formik.values.effectiveDate).format('YYYY-MM-DD') : undefined}
                        carrierName={formik.values.carrierName || ''}
                        disabled={!formik.values.carrierName}
                        age={adjustAge(String(formik.values.age) || '', formik.values.birthDate, formik.values.effectiveDate)}
        />
      </Box>

      <FormikSelectInput formik={formik}
                         sx={{
                           '& .MuiListItemText-primary': {
                             overflow: 'hidden',
                             textOverflow: 'ellipsis'
                           }
                         }}
                         defaultValue={''}
                         onChange={(event: any) => {
                           formik.setFieldValue("enrollmentType", event.target.value as string);
                         }}
                         name={'enrollmentType'}>
        {[MedigapEnrollmentType.MgOep, MedigapEnrollmentType.MgUnderwritten, MedigapEnrollmentType.MgGi].map(type => (
          <MenuItem key={type} onClick={() => {
            formik.setFieldValue("enrollmentType", type)
          }} value={type as string}>
            <ListItemText sx={{m: 0}} primary={getMgEnrollmentTypeTitle(type)} secondary={getMgEnrollmentTypeSubTitle(type)} />
          </MenuItem>
        ))}
      </FormikSelectInput>

      {!!formik.values['enrollmentType'] && <Typography variant={'caption'}>
        Commission paid as and when premiums are paid by multiplying commission rate % by monthly premium
      </Typography>}



      <Stack gap={2} direction={'row'} alignItems={'center'} justifyContent={'flex-end'} mt={2}>
        <Button onClick={() => onSubmit()} variant={'outlined'}>
          Cancel
        </Button>
        <LoadingButton
          loading={saveLoading || changeStatusLoading}
          type="submit"
          variant={'contained'}
        >
          Save
        </LoadingButton>
      </Stack>
    </Stack>
  );
};

interface MgEnrollmentFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (refresh?: boolean) => void;
  enrollment?: Partial<EnrollmentListItemOutput>
}

export const MgEnrollmentFormDialog = (props: MgEnrollmentFormDialogProps) => (
  <Dialog fullWidth maxWidth={'md'} open={props.open} onClose={props.onClose}>
    <DialogTitle variant={'h3'}>
      {props.enrollment?.id ? 'Edit' : 'Create'} enrollment
      <ModalCloseButton onClick={props.onClose} sx={{position: 'absolute', top: 4, right: 8}} />
    </DialogTitle>
    <DialogContent>
      <MgEnrollmentForm enrollment={props.enrollment} onSubmit={props.onSubmit} />
    </DialogContent>
  </Dialog>
)

export const MgEnrollmentEditButton = forwardRef<HTMLDivElement, {onChange: () => void, enrollment: EnrollmentListItemOutput}>((props, ref) => {
  const [open, setOpen] = useState(false);
  const {enrollment, onChange, ...rest} = props;
  const [deleteEnrollment] = useDeleteMedigapEnrollmentMutation()
  const confirm = useConfirm(theme);
  const [copy, setCopy] = useState<Partial<EnrollmentListItemOutput>>();

  const handleDeleteClick = () => {
    confirm({
      content: 'Are you sure you want to delete this enrollment?',
      okButtonTitle: 'Yes',
      cancelButtonTitle: 'No',
      title: 'Attention!',
    })
      .then(val => {
        if (val) {
          deleteEnrollment({
            variables: {medigapEnrollmentId: enrollment.id}
          }).then(onChange)
        }
      })
  }

  const handleCopyClick = () => {
    setCopy(_.omit(enrollment, ['__typename', 'id', 'createdAt', 'updatedAt']))
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setCopy(undefined);
  }

  return <>
    <MgEnrollmentFormDialog enrollment={copy || enrollment}
                            onSubmit={refresh => {
                              handleClose()
                              refresh && onChange()
                            }}
                            open={open}
                            onClose={handleClose} />
    <Stack ref={ref} gap={1} {...rest}>
      <IconButton size={'small'} onClick={e => {
        setOpen(true)
      }}>
        <Pen color="#a6a6a6" />
      </IconButton>
      <IconButton size={'small'} onClick={handleCopyClick}>
        <Copy color="#a6a6a6" />
      </IconButton>
      <IconButton size={'small'} onClick={handleDeleteClick}>
        <X color={'#DE3124'} />
      </IconButton>
    </Stack>

  </>
})

const validationSchema = yup.object({
  zip: yup.string().required('Zip is required').default(''),
  county: yup.string().required('County is required').default(''),
  firstName: yup
    .string()
    .required('First name is required')
    .default(''),
  lastName: yup
    .string()
    .required('Last name is required')
    .default(''),
  email: yup
    .string()
    .email('Enter a valid email')
    .required('Email is required')
    .default(''),
  status: yup
    .string()
    .required('Status is required')
    .default(EnrollmentStatusDto.Draft),
  planName: yup
    .string()
    .default(''),
  phoneNumber: yup
    .string()
    .required('Phone is required')
    .typeError('Please enter a valid phone number')
    .default(''),
  enrollmentType: yup
    .string().nullable()
    .default(''),
  agentId: yup
    .string()
    .required('Agent is required')
    .default(''),
  birthDate: yup
    .string()
    .required('Birth date is required')
    .typeError('Please enter a valid date'),
  applicationDate: yup
    .string()
    .required('Application date is required')
    .typeError('Please enter a valid date'),
  effectiveDate: yup
    .string()
    .required('Effective date is required')
    .test('effectiveDate', 'Date is not valid', (value, context) => (
      !value || (context as any)['from'][0]?.value?.id ? true : isEffectiveDateValid(value)
    ))
    .typeError('Please enter a valid date'),
  naic: yup
    .string()
    .required('Carrier is required'),
  age: yup
    .string()
    .required('Age is required'),
  planKey: yup
    .string()
    .required('Plan is required'),
  tobacco: yup
    .boolean()
    .nullable()
    .required('Tobacco is required')
    .default(null)
  ,
  gender: yup
    .string()
    .required('Gender is required'),
});

const initial = {
  zip: '',
  county: '',
  firstName: '',
  lastName: '',
  email: '',
  status: '',
  bidId: '',
  carrierName: '',
  planName: '',
  phoneNumber: '',
  enrollmentType: '',
  agentId: '',
  birthDate: '',
  applicationDate: '',
  effectiveDate: '',
  naic: '',
  age: '' as any,
  mgPlanKey: '',
  tobacco: '' as any,
  gender: '' as any,
}
